// Voice Sample Worksheet - Dashboard Integrated Version
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Image,
  Segment,
  Header,
  Button,
  Icon,
  Divider,
  Card,
  Tab,
  Message,
  Statistic
} from 'semantic-ui-react';
import { AuthStdFooter } from '../../../utils/auth-std-footer';
import { v4 as uuidv4 } from 'uuid';
import NotificationPopup from '../../../components/NotificationPopup';
import SampleCharactersList from './components/SampleCharactersList';
import SampleCharacterForm from './components/SampleCharacterForm';
import {
  fetchPrimaryPortfolio,
  fetchSampleCharacters,
  createSampleCharacterComplete,
  updateSampleCharacterComplete,
  deleteSampleCharacter,
  selectSampleCharacter,
  setCreatingNew,
  selectPrimaryPortfolio,
  selectSampleCharacters,
  selectSelectedSampleCharacter,
  selectIsCreatingNew,
  selectLoadingStates,
  selectErrors
} from '../../../slices/sampleCharacterSlice';

// Import the same portfolio image as MyPortfolio
import portfolioImage from '../../../assets/shutterstock_2294366549-480x480.png';
// Import icons for statistics and quick actions
import profileIcon from '../../../assets/profileLineGlasses.svg';
import myProfileLine from '../../../assets/myProfileLine.svg';
import lightening from '../../../assets/lightening.svg';
import bookStack from '../../../assets/justBookStack.svg';
import voiceActressBW from '../../../assets/micAndshield.svg';
import microphoneStand from '../../../assets/microphone-stand.svg'

// Import required styles
import '../../../styles/landing-pages.css';
import '../../../styles/article.css';
import '../../../styles/sample-voice-pages.css';
import './voiceSampleWorksheet.css';

const VoiceSampleWorksheet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL parameters for deep-linking from ApplicationSubmission
  // Support both URL search params (direct link) and location.state (dashboard navigation)
  const locationState = window.history.state?.usr || {};
  const newCharacterParam = searchParams.get('newCharacter') === 'true' || locationState?.newCharacter === true;
  const returnToParam = searchParams.get('returnTo') || locationState?.returnTo || null;
  const deepLinkHandled = useRef(false);

  // Redux state
  const currentUser = useSelector(state => state.user.profile?.data);
  const primaryPortfolio = useSelector(selectPrimaryPortfolio);
  const sampleCharacters = useSelector(selectSampleCharacters);
  const selectedCharacter = useSelector(selectSelectedSampleCharacter);
  const isCreatingNew = useSelector(selectIsCreatingNew);
  const loading = useSelector(selectLoadingStates);
  const errors = useSelector(selectErrors);

  // Local state
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [showPortfolioError, setShowPortfolioError] = useState(false);
  const [newCharacterId, setNewCharacterId] = useState(uuidv4());
  const [formKey, setFormKey] = useState(Date.now());
  const [isSaving, setIsSaving] = useState(false);

  // Get actor ID from current user
  const actorId = currentUser?.actorId || currentUser?.userId;
  const userName = currentUser?.firstName && currentUser?.lastName
    ? `${currentUser.firstName} ${currentUser.lastName}`
    : currentUser?.title || 'Voice Actor';

  // Initialize data on mount
  useEffect(() => {
    if (actorId) {
      dispatch(fetchPrimaryPortfolio(actorId));
    }
  }, [dispatch, actorId]);

  // Fetch sample characters when portfolio is loaded
  useEffect(() => {
    if (primaryPortfolio?.uuid) {
      dispatch(fetchSampleCharacters(primaryPortfolio.uuid));
    }
  }, [dispatch, primaryPortfolio?.uuid]);

  // Update form key when selected character changes for proper re-rendering
  useEffect(() => {
    setFormKey(Date.now());
  }, [selectedCharacter?.uuid, selectedCharacter?.sample?.textContent]);

  // Handle portfolio error notifications
  useEffect(() => {
    if (errors.portfolio) {
      setShowPortfolioError(true);
    }
  }, [errors.portfolio]);

  // Handle success message notifications
  useEffect(() => {
    if (successMessage) {
      setShowSuccessNotification(true);
    }
  }, [successMessage]);

  // Handle error message notifications
  useEffect(() => {
    if (errorMessage) {
      setShowErrorNotification(true);
    }
  }, [errorMessage]);

  // Deep-link: auto-trigger new character creation when ?newCharacter=true
  useEffect(() => {
    if (newCharacterParam && primaryPortfolio?.uuid && !deepLinkHandled.current) {
      deepLinkHandled.current = true;
      console.log('[VoiceSampleWorksheet] Deep-link: auto-triggering new character creation');
      handleNewCharacter();
      // Clean the newCharacter param from URL (keep returnTo)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('newCharacter');
      setSearchParams(newParams, { replace: true });
    }
  }, [newCharacterParam, primaryPortfolio?.uuid]);

  // Handle returnTo navigation after successful save
  const handleReturnToApplication = () => {
    if (returnToParam) {
      navigate(decodeURIComponent(returnToParam));
    }
  };

  // Calculate portfolio statistics
  const calculatePortfolioStats = () => {
    const totalCharacters = sampleCharacters.length;
    // Check both new sample structure and legacy variations
    const recordedSamples = sampleCharacters.filter(char =>
      char.sample?.voiceS3Url || (char.variations && char.variations.some(v => v.s3Url))
    ).length;
    const characterTypes = [...new Set(sampleCharacters.map(c => c.type).filter(Boolean))].length;

    // Calculate completion percentage
    const requiredFields = ['name', 'type', 'gender', 'ageGroup'];
    const completedCharacters = sampleCharacters.filter(char =>
      requiredFields.every(field => char[field])
    ).length;
    const completionPercentage = totalCharacters > 0
      ? Math.round((completedCharacters / totalCharacters) * 100)
      : 0;

    return {
      totalCharacters,
      recordedSamples,
      characterTypes,
      completionPercentage,
      portfolioName: primaryPortfolio?.name || 'Default Portfolio'
    };
  };

  const portfolioStats = calculatePortfolioStats();

  // Statistics for the overview section
  const statistics = [
    {
      label: 'Voice Samples',
      value: portfolioStats.totalCharacters.toString(),
      icon: 'users',
      img: profileIcon
    },
    {
      label: 'Recorded Samples',
      value: portfolioStats.recordedSamples.toString(),
      icon: 'microphone',
      img: microphoneStand
    },
    {
      label: 'Character Types',
      value: portfolioStats.characterTypes.toString(),
      icon: 'tags',
      img: bookStack
    }
    ,
    // {
    //   label: 'Completion',
    //   value: `${portfolioStats.completionPercentage}%`,
    //   icon: 'check circle',
    //   img: checkBox
    // }
    // ,
    // {
    //   label: 'Portfolio',
    //   value: portfolioStats.portfolioName,
    //   icon: 'folder',
    //   img: profileIcon
    // }
  ];

  // Quick actions configuration
  const quickActions = [
    {
      icon: 'plus',
      img: microphoneStand,
      title: 'Create New Character',
      description: 'Add a new sample character',
      action: () => handleNewCharacter()
    },
    {
      icon: 'microphone',
      img: voiceActressBW,
      title: 'Recording Studio',
      description: 'Record voice samples',
      action: () => {
        if (selectedCharacter || isCreatingNew) {
          setActiveTabIndex(1);
        } else if (sampleCharacters.length > 0) {
          handleSelectCharacter(sampleCharacters[0]);
        }
      }
    },
    {
      icon: 'users',
      img: profileIcon,
      title: 'Manage Characters',
      description: 'View and edit existing characters',
      action: () => setActiveTabIndex(0)
    },
    {
      icon: 'folder open',
      img: bookStack,
      title: 'View Portfolio',
      description: 'Go to your main portfolio',
      link: '/dashboard',
      state: { menuSelection: 'MY-PORTFOLIO' }
    }
  ];

  // Handle new character button
  const handleNewCharacter = () => {
    dispatch(setCreatingNew(true));
    setNewCharacterId(uuidv4());
    setActiveTabIndex(1);
  };

  // Handle character selection
  const handleSelectCharacter = (character) => {
    dispatch(selectSampleCharacter(character));
    setActiveTabIndex(1);
  };

  // Handle character deletion
  const handleDeleteCharacter = (character) => {
    setCharacterToDelete(character);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (characterToDelete) {
      try {
        await dispatch(deleteSampleCharacter({
          characterId: characterToDelete.uuid,
          cascadeDelete: true
        })).unwrap();

        setSuccessMessage('Character deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Failed to delete character:', error);
      }
    }
    setShowDeleteModal(false);
    setCharacterToDelete(null);
  };

  // Handle save character - Now using atomic transaction endpoints
  const handleSaveCharacter = async (formData, audioData) => {
    console.log('[handleSaveCharacter] Called with formData:', formData);
    console.log('[handleSaveCharacter] Called with audioData:', audioData);

    // Prevent double saves
    if (isSaving) {
      console.log('[handleSaveCharacter] Already saving, ignoring duplicate call');
      return false;
    }

    setIsSaving(true);
    setErrorMessage('');

    try {
      // Track what's actually changing - use consistent 'sample' structure
      const originalTextContent = selectedCharacter?.sample?.textContent || '';
      const hasTextChanged = formData.scriptContent !== originalTextContent;
      const hasNewAudio = !!audioData?.blob;

      // Only create mediaData if there are actual media changes
      let mediaData = null;
      if (hasNewAudio || hasTextChanged) {
        // Convert audio blob to base64 if present
        let audioBase64 = null;
        if (audioData?.blob) {
          try {
            // Convert Blob to base64
            const reader = new FileReader();
            audioBase64 = await new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(audioData.blob);
            });
            console.log('[handleSaveCharacter] Converted audio blob to base64');
          } catch (error) {
            console.error('[handleSaveCharacter] Failed to convert audio blob:', error);
            setErrorMessage('Failed to process audio recording');
            setIsSaving(false);
            return false;
          }
        }

        mediaData = {
          // Explicit change flags
          hasAudioChange: hasNewAudio,
          hasTextChange: hasTextChanged,

          // Audio data (only if new audio) - now as base64 string
          audioBlob: audioBase64,
          fileDuration: audioData?.duration || null,
          fileName: audioData?.fileName || 'recording.webm',

          // Text data (always include current value)
          textContent: formData.scriptContent,

          // Preserve existing URLs for backend reference
          existingVoiceUrl: selectedCharacter?.sample?.voiceS3Url,
          existingTextUrl: selectedCharacter?.sample?.textS3Url,
          existingDuration: selectedCharacter?.sample?.fileDuration
        };
      }

      if (isCreatingNew) {
        // Use atomic create endpoint - handles character + media in one transaction
        const result = await dispatch(createSampleCharacterComplete({
          portfolioId: primaryPortfolio.uuid,
          characterData: {
            ...formData,
            uuid: newCharacterId
          },
          mediaData
        })).unwrap();

        console.log('[ATOMIC CREATE] Character created with media:', result);

        // Reset state after successful create
        dispatch(setCreatingNew(false));
        dispatch(selectSampleCharacter(result));

        // Refresh character list
        await dispatch(fetchSampleCharacters(primaryPortfolio.uuid));

        setSuccessMessage('Character created successfully');
        setActiveTabIndex(0);
      } else if (selectedCharacter) {
        // Use atomic update endpoint - handles character + media in one transaction
        console.log('[DEBUG] Dispatching updateSampleCharacterComplete with:', {
          portfolioId: primaryPortfolio.uuid,
          characterId: selectedCharacter.uuid,
          hasMediaData: !!mediaData
        });

        console.log('[handleSaveCharacter] About to dispatch updateSampleCharacterComplete');
        const result = await dispatch(updateSampleCharacterComplete({
          portfolioId: primaryPortfolio.uuid,
          characterId: selectedCharacter.uuid,
          characterData: formData,
          mediaData
        })).unwrap();

        console.log('[ATOMIC UPDATE] Character updated with media:', result);
        console.log('[ATOMIC UPDATE] Result has sample data?', result?.sample);

        // Refresh character list after update
        await dispatch(fetchSampleCharacters(primaryPortfolio.uuid));

        setSuccessMessage('Character updated successfully');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
      return true;
    } catch (error) {
      console.error('Failed to save character:', error);

      // Set user-friendly error message
      const errorMsg = error?.message || error?.error || 'Failed to save character. Please try again.';
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Render character list tab content
  const renderCharactersList = () => (
    <Tab.Pane>
      <SampleCharactersList
        characters={sampleCharacters}
        loading={loading.characters}
        onSelectCharacter={handleSelectCharacter}
        onDeleteCharacter={handleDeleteCharacter}
        onNewCharacter={handleNewCharacter}
        selectedCharacter={selectedCharacter}
      />
    </Tab.Pane>
  );

  // Render recording studio tab content
  const renderRecordingStudio = () => (
    <Tab.Pane>
      {(selectedCharacter || isCreatingNew) ? (
        <SampleCharacterForm
          key={`${selectedCharacter?.uuid || newCharacterId}-${formKey}`}
          character={selectedCharacter}
          isCreatingNew={isCreatingNew}
          newCharacterId={newCharacterId}
          onSave={handleSaveCharacter}
          onCancel={() => {
            setActiveTabIndex(0);
            dispatch(setCreatingNew(false));
          }}
          loading={loading.create || loading.update || isSaving}
          portfolioId={primaryPortfolio?.uuid}
        />
      ) : (
        <Segment placeholder>
          <Header icon>
            <Icon name="microphone slash" />
            No Character Selected
          </Header>
          <p>Please select a character from the Characters tab or create a new one to start recording.</p>
          <div>
            <Button primary onClick={handleNewCharacter}>
              <Icon name="plus" />
              Create New Character
            </Button>
            {sampleCharacters.length > 0 && (
              <Button onClick={() => setActiveTabIndex(0)}>
                <Icon name="users" />
                Select Existing Character
              </Button>
            )}
          </div>
        </Segment>
      )}
    </Tab.Pane>
  );

  // Define tabs
  const panes = [
    {
      menuItem: { key: 'characters', icon: 'users', content: 'Sample Characters' },
      render: renderCharactersList
    },
    {
      menuItem: {
        key: 'recording',
        icon: 'microphone',
        content: 'Recording Studio',
        disabled: !selectedCharacter && !isCreatingNew
      },
      render: renderRecordingStudio
    }
  ];

  return (
    <div className="landing-page voice-sample-worksheet listContainer">
      {/* Header Section - Matches MyPortfolio */}
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src={portfolioImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Sample Manager</span>
              <span className="blgListSubTitle">Create and manage your voice sample characters</span>
              <p className="landing-subheading-text">
                Create and manage sample characters for your voice acting portfolio.
                Record demos, showcase your range, and organize your voice samples all in one place.
              </p>
              {/* <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div> */}
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="headerBottomLine" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      {/* Return to Application Banner - shown when navigated from ApplicationSubmission */}
      {returnToParam && (
        <Container style={{ marginTop: '1em' }}>
          <Message info icon>
            <Icon name="arrow left" />
            <Message.Content>
              <Message.Header>You came from an application</Message.Header>
              <p style={{ margin: '5px 0' }}>
                Record a voice sample, then return to continue your application.
              </p>
              <Button
                size="small"
                color="blue"
                icon="arrow left"
                content="Return to Application"
                onClick={handleReturnToApplication}
              />
            </Message.Content>
          </Message>
        </Container>
      )}

      {/* Notification Popups - Overlay on the right side */}
      <NotificationPopup
        message={errors.portfolio}
        type="error"
        header="Error Loading Portfolio"
        visible={showPortfolioError}
        onClose={() => {
          setShowPortfolioError(false);
        }}
        duration={0} // No auto-dismiss for portfolio errors
      />

      <NotificationPopup
        message={errorMessage}
        type="error"
        header="Error"
        visible={showErrorNotification}
        onClose={() => {
          setShowErrorNotification(false);
          setErrorMessage('');
        }}
      />

      <NotificationPopup
        message={successMessage}
        type="success"
        header="Success"
        visible={showSuccessNotification}
        onClose={() => {
          setShowSuccessNotification(false);
          setSuccessMessage('');
        }}
      />

      {!primaryPortfolio && !loading.portfolio ? (
        <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
          <Segment placeholder>
            <Header icon>
              <Icon name="folder open outline" />
              No Portfolio Found
            </Header>
            <p>You need to create a portfolio first before adding sample characters.</p>
            <Button primary as={Link} to="/dashboard" state={{ menuSelection: 'VOICE-ACTOR-WORKSHEET-ENH' }}>
              Go to Voice Actor Worksheet
            </Button>
          </Segment>
        </Container>
      ) : loading.portfolio ? (
        <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
          <Message icon>
            <Icon name='circle notched' loading />
            <Message.Content>
              <Message.Header>Just a moment</Message.Header>
              Loading your portfolio...
            </Message.Content>
          </Message>
        </Container>
      ) : (
        <>
          {/* Portfolio Overview Section - Matches MyPortfolio */}
          <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={7}>
                  <Header as="h2">
                    <img src={myProfileLine} alt="" />
                    <Header.Content style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '400',
                      fontSize: '2.5rem'
                    }}>
                      Portfolio Overview
                      <Header.Subheader>Your sample characters at a glance</Header.Subheader>
                    </Header.Content>
                  </Header>
                </Grid.Column>
                <Grid.Column width={9}>
                  <div>
                    <Statistic.Group widths="3">
                      {statistics.map((stat, index) => (
                        <Statistic key={index}>
                          <Statistic.Value>
                            <div style={{
                              fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                              fontWeight: '300',
                              fontSize: '3.0rem'
                            }}>
                              <img src={stat.img} alt="" style={{ height: '40px', marginRight: '10px' }} />
                              {stat.value}
                            </div>
                          </Statistic.Value>
                          <Statistic.Label style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '1.1rem'
                          }}>
                            {stat.label}
                          </Statistic.Label>
                        </Statistic>
                      ))}
                    </Statistic.Group>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Divider />

            {/* Quick Actions Section */}
            <Header as="h2" dividing style={{ marginTop: '2em' }}>
              <img src={lightening} alt="" />
              <Header.Content>
                Quick Actions
                <Header.Subheader>Manage your sample characters</Header.Subheader>
              </Header.Content>
            </Header>

            <Card.Group itemsPerRow={4} stackable>
              {quickActions.map((action, index) => (
                <Card
                  key={index}
                  onClick={action.action}
                  as={action.link ? Link : 'div'}
                  to={action.link}
                  state={action.state}
                  raised
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Content>
                    <img
                      src={action.img}
                      alt=""
                      style={{
                        height: '80px',
                        display: 'block',
                        margin: '0 auto 1em',
                        color: '#2c5d8a'
                      }}
                    />
                    <Card.Header textAlign="center">{action.title}</Card.Header>
                    <Card.Description textAlign="center">
                      {action.description}
                    </Card.Description>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>

            {/* Main Content Tabs */}
            <Grid columns={1} stackable style={{ marginTop: '2em' }}>
              <Grid.Column>
                <Header as="h2" dividing>
                  <img src={microphoneStand} alt="" style={{ height: '50px' }} />
                  <Header.Content className="sectionTitle">
                    Sample Character Management
                    <Header.Subheader>Create and manage your voice sample characters</Header.Subheader>
                  </Header.Content>
                </Header>

                <Tab
                  panes={panes}
                  activeIndex={activeTabIndex}
                  onTabChange={(e, { activeIndex }) => setActiveTabIndex(activeIndex)}
                />
              </Grid.Column>
            </Grid>
          </Container>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && characterToDelete && (
        <div className="ui dimmer modals page transition visible active">
          <div className="ui small modal transition visible active">
            <div className="header">
              <Icon name="trash" />
              Delete Sample Character
            </div>
            <div className="content">
              <p>Are you sure you want to delete <strong>{characterToDelete.name}</strong>?</p>
              <p>This will also delete all associated recordings.</p>
            </div>
            <div className="actions">
              <Button onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button negative onClick={confirmDelete}>
                <Icon name="trash" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {AuthStdFooter()}
    </div>
  );
};

export default VoiceSampleWorksheet;
